import React from "react";
import { IListing } from "../../lib/Listing/types";
import { EWsrvTransform } from "../../lib/Media/util";
import { ITabContent, TabbedContentBlade } from "../Blades/TabbedContentBlade";
import { Carousel } from "../Carousel/Carousel";
import { ContactForm } from "../Forms";
import { ListingGallery } from "../Gallery/ListingGallery";
import { BackgroundImage } from "../Media";
import { ListingCard, EListingCardLayouts } from "./ListingCard";
import { ListingContact } from "./ListingContact";
import ListingDetails from "./ListingDetails";
import { ListingMap } from "./ListingMap";
import { ListingOpenHouse } from "./ListingOpenHouse";
import { ListingSummary } from "./ListingSummary";
import { EListingVitalsLayout, ListingVitals } from "./ListingVitals";
import { EDisclaimerLocation, IListingLayoutProps } from "./util";

export const ListingLayoutGalleryContact: React.FC<IListingLayoutProps> = ({
  listing,
  title,
  linkFunc,
  similar,
  agents,
  className = "",
  similarCardProps,
  similarCarouselProps,
  vitalsProps,
  contactProps,
  summaryProps,
  galleryProps,
  detailsProps,
  formProps,
  mapProps,
  rowClassName = "",
  showContactUsForm,
  contactUsFormProps,
  displayDisclaimer = false,
  disclaimerCssClasses = "",
  disclaimers,
  displayMediaSection,
  photosTabTitle,
  propertyVideoTabTitle,
  virtualTourTabTitle,
  floorplansTabTitle,
  headingProps,
  formColumnClassName = "",
  openHouseProps,
  showDivider = false,
  dividerClassName = "",
  disclaimerLocation = EDisclaimerLocation.bottom,
  displayCourtesyUnderGallery = false,
  showForm = true,
}) => {

  let MediaSection: React.FC<IListing> = () => { return (<></>) }
  displayMediaSection && (
    MediaSection = (listing) => {
      if (listing.videos || listing.tours || listing.floorplans) {
        let tabContent: ITabContent[] = [
          {
            tabTitle: photosTabTitle ?? "Photos",
            contentClassName: "max-w-full",
            content: (
              <ListingGallery
                listing={listing}
                preset={EWsrvTransform.cover16x9}
                width={1600}
                height={900}
                {...galleryProps}
              />
            ),
          },
        ];
        if (listing.videos && listing.videos.length > 0) {
          tabContent.push({
            tabTitle: propertyVideoTabTitle ?? "Property Video",
            contentClassName: "max-w-full w-full",
            content: (
              <iframe
                src={listing.videos[0].url}
                allowFullScreen
                allow="encrypted-media"
                className="w-full aspect-video"
              />
            ),
          });
        }
        if (listing.tours && listing.tours.length > 0) {
          tabContent.push({
            tabTitle: virtualTourTabTitle ?? "Virtual Tour",
            contentClassName: "max-w-full w-full",
            content: (
              <iframe
                src={listing.tours[0].url}
                className="w-full aspect-video"
              />
            ),
          });
        }
        if (listing.floorplans && listing.floorplans.length > 0) {
          tabContent.push({
            tabTitle: floorplansTabTitle ?? "Floor Plans",
            content: (
              <>
                {listing.floorplans.map((plan, index) => (
                  <img src={plan.url} key={index} />
                ))}
              </>
            ),
          });
        }
        return (
          <TabbedContentBlade
            title="Media"
            className="max-w-screen-xl mx-auto"
            tabListClassName="inverted"
            tabs={tabContent}
          />
        );
      } else {
        return (
          <ListingGallery
            listing={listing}
            preset={EWsrvTransform.cover16x9}
            width={1600}
            height={900}
            {...galleryProps}
          />
        );
      }
    }
  );

  const Disclaimer: React.FC = () => {
    return (
      <>
        {displayDisclaimer && (
          <section className={disclaimerCssClasses} dangerouslySetInnerHTML={{ __html: disclaimers?.find(x => x.mlsSourceName === listing.mlsSourceName)?.disclaimerText ?? "" }}>
          </section>
        )}
      </>
    )
  }

  return (
    <div className={`${className} listing-layout layout-gallery-contact`}>
      {headingProps?.src && (
        <BackgroundImage
          {...headingProps}
        >
        </BackgroundImage>
      )}
      <div
        className={`flex flex-col lg:grid lg:grid-cols-3 lg:space-x-5 my-5 ${rowClassName}`}
      >
        <ListingGallery
          listing={listing}
          preset={EWsrvTransform.cover3x2}
          width={900}
          height={600}
          className={`lg:col-span-2 mb-3 ${displayCourtesyUnderGallery ? "order-first" : ""}`}
          {...galleryProps}
        />
        <div className={`${displayCourtesyUnderGallery ? "order-last lg:order-2 " : ""}lg:col-span-1 mb-3 ${formColumnClassName}`}>
          <ListingVitals listing={listing} className="mb-3" {...vitalsProps} />
          <ListingOpenHouse openHouse={listing.openHouse} className="mb-3" {...openHouseProps} />
          {showDivider && (<div className={`w-full h-px bg-divider ${dividerClassName}`}></div>)}
          {showForm && <ContactForm {...formProps}>
            <ListingContact
              listing={listing}
              agents={agents}
              linkFunc={linkFunc}
              {...contactProps}
            />
          </ContactForm>}
        </div>
        {displayCourtesyUnderGallery && <div className="order-3 flex ml-0 lg:col-span-3 flex-wrap listing-courtesy gap-4 text-xs py-3 italic whitespace-pre-wrap w-full">
          <span>Listing Courtesy of{"\n"}<span className="font-bold">{listing.officeName}</span></span>
        </div>}
      </div>
      {title && <h1>{title}</h1>}
      <ListingSummary
        listing={listing}
        className="my-5 py-3"
        {...summaryProps}
      />
      {<MediaSection {...listing} />}
      {listing.details && (
        <ListingDetails
          detailGroups={listing.details}
          className="py-3"
          {...detailsProps}
        />
      )}
      {displayDisclaimer && disclaimerLocation == EDisclaimerLocation.afterDetails && (
        <Disclaimer />
      )}
      {listing.location && listing.location?.lat && listing.location.lng && (
        <ListingMap
          listing={listing}
          title="Location"
          className="my-5 py-3"
          {...mapProps}
        />
      )}
      {similar && similar.length > 2 && (
        <Carousel
          title="Similar"
          slickSettings={{
            centerMode: true,
            infinite: true,
            centerPadding: "10px",
            slidesToShow: Math.min(3, similar.length),
            arrows: true,
          }}
          className={`slick-center-zoom ${similarCarouselProps?.className || ""
            }`}
          {...similarCarouselProps}
        >
          {similar.map((listing, index) => {
            return (
              <ListingCard
                layout={EListingCardLayouts.vertical}
                vitalsLayout={EListingVitalsLayout.column2Border1}
                listing={listing}
                linkFunc={linkFunc}
                className="max-w-[680px]"
                maxWidthImage={680}
                key={index}
                {...similarCardProps}
              />
            );
          })}
        </Carousel>
      )}
      {showContactUsForm && <ContactForm {...contactUsFormProps} />}
      {displayDisclaimer && disclaimerLocation == EDisclaimerLocation.bottom && (
        <Disclaimer />
      )}
    </div>
  );
};
